import React, { useState } from "react";
import { HelpIndicator, Text } from "@jsq/ds/core";
import { FormTextInput } from "@jsq/ds/forms";
import { Icon } from "@jsq/ds/icons";
import { FieldGroup } from "../../../../../common/components/Form/Layout/FieldGroup";
import { MultiInputFormRow } from "../../../../../common/components/Form/Layout/MultiInputFormRow";
import { SimpleFormRow } from "../../../../../common/components/Form/Layout/SimpleFormRow";
import { isUSA } from "../../../../../common/utils";
import { SharedAddressInput } from "../../../../SharedAddressInput";
import { getNameFromCountry } from "../../../mappers";
import { ExpandableSection } from "../../ExpandableSection";
import {
  AccountDetailsInputFields,
  type AccountDetailsInputType,
  WireExtraFieldInputFields,
} from "./constants";
import { ExtraInputFields } from "./ExtraInputFields";
import { informationForRecipient } from "./styles_vanilla.css";
import { WireAddressCountrySelect } from "./WireAddressCountrySelect";

export const AccountDetails: React.FC = () => {
  const [showExtraInfo, setShowExtraInfo] = useState(false);

  return (
    <FieldGroup title="Account details">
      <SimpleFormRow>
        <FormTextInput
          label="Account holder name"
          isFullWidth
          name={AccountDetailsInputFields.AccountHolderName}
        />
      </SimpleFormRow>
      <MultiInputFormRow>
        <FormTextInput
          label="Account number"
          isFullWidth
          name={AccountDetailsInputFields.AccountNumber}
        />
        <FormTextInput
          label="Confirm account number"
          isFullWidth
          name={AccountDetailsInputFields.ConfirmAccountNumber}
        />
      </MultiInputFormRow>
      <SharedAddressInput
        title="Account holder address"
        meta={{ headerType: "strong-md" }}
        CountrySelect={WireAddressCountrySelect}
        testIsUSA={(country) => isUSA(getNameFromCountry(country))}
      />
      <ExpandableSection<AccountDetailsInputType>
        buttonLabel="Information for recipient"
        setIsExpanded={setShowExtraInfo}
        isExpanded={showExtraInfo}
        className={informationForRecipient}
        errorFocusFieldNames={[
          AccountDetailsInputFields.ReferenceToBeneficiary,
          `${AccountDetailsInputFields.BBI}.${WireExtraFieldInputFields.Field0}`,
          `${AccountDetailsInputFields.BBI}.${WireExtraFieldInputFields.Field1}`,
          `${AccountDetailsInputFields.BBI}.${WireExtraFieldInputFields.Field2}`,
          `${AccountDetailsInputFields.BBI}.${WireExtraFieldInputFields.Field3}`,
          `${AccountDetailsInputFields.OBI}.${WireExtraFieldInputFields.Field0}`,
          `${AccountDetailsInputFields.OBI}.${WireExtraFieldInputFields.Field1}`,
          `${AccountDetailsInputFields.OBI}.${WireExtraFieldInputFields.Field2}`,
          `${AccountDetailsInputFields.OBI}.${WireExtraFieldInputFields.Field3}`,
        ]}
      >
        <ExtraInputFields fieldType={AccountDetailsInputFields.OBI} />
        <SimpleFormRow>
          <FormTextInput
            name={AccountDetailsInputFields.ReferenceToBeneficiary}
            isFullWidth
            label={
              <HelpIndicator
                id="wire-reference-help"
                placement="right"
                data-cy="help-icon"
                content="Additional information such as an internal account number. This field is not always received by the recipient."
              >
                <Text>
                  Reference to beneficiary{" "}
                  <Icon type="question-circle" size="small" variant="outline" />{" "}
                </Text>
              </HelpIndicator>
            }
          />
        </SimpleFormRow>
        <ExtraInputFields fieldType={AccountDetailsInputFields.BBI} />
      </ExpandableSection>
    </FieldGroup>
  );
};
